import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { api, ApiError } from "../../api/client";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Input } from "../../components/Input";
import { useWholeMoneyField } from "../../components/MoneyInput";
import { useToast } from "../../components/Toast";
import type { CatalogHit } from "../../api/types";

// 品項庫的編輯(業主原話:「品項庫 無法刪除/編輯 已加入的品項」)。
//
// 四個欄位:品名、規格、單位、單價。**分類不在這裡改** —— 後端的 PATCH 不收它,
// 擺一格改不動的欄位只會讓人以為改得動,改完才發現分類沒變。要換分類就在
// 新的分類底下另外建一筆。
//
// 改單價**不會回頭改動已經開出去的單**:明細行上的單價是存檔當下複寫進去的
// 快照,不是指向品項庫的參照(設計規格 §5.5 的價格快照規則)。那條紀律守在
// 後端,這裡只負責把修改送對地方。
//
// 品名改的是**品項那一層**:同一個品名底下的每一種規格會一起改名,
// 因為那本來就是同一個品項 —— 說明文字要講出來,不然使用者以為只改了這一筆。

export interface CatalogEditDialogProps {
  /** 要編輯的那一筆;null 代表關閉。 */
  hit: CatalogHit | null;
  onClose: () => void;
}

export function CatalogEditDialog({ hit, onClose }: CatalogEditDialogProps) {
  const { push } = useToast();
  const queryClient = useQueryClient();

  const [itemName, setItemName] = useState("");
  const [spec, setSpec] = useState("");
  const [unit, setUnit] = useState("");
  const [price, setPrice] = useState("0");
  const [error, setError] = useState<string | null>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  // 單價欄的行為(千分位、整數化、游標還原)一律走 MoneyInput 那一份實作,
  // 與新增對話框同一套。兩份平行實作遲早會對同一串輸入給出兩種答案。
  const priceField = useWholeMoneyField(price, setPrice);

  // 每次開啟都以那一列現在的值重新填滿,上一次改到一半的東西不會殘留。
  useEffect(() => {
    if (hit === null) return;
    setItemName(hit.item_name);
    setSpec(hit.spec);
    setUnit(hit.unit);
    setPrice(hit.price);
    setError(null);
  }, [hit]);

  // 開啟後把焦點送進第一格。**刻意不用 autoFocus**:那是在 commit 階段就搶焦點,
  // 早於 Dialog 的 useRestoreFocus 記下「原本焦點在哪顆按鈕」,關閉時焦點會掉到
  // <body>,鍵盤使用者按完 Esc 得從頁首重新 Tab 一次才回得到品項庫。
  useEffect(() => {
    if (hit === null) return;
    firstFieldRef.current?.focus();
  }, [hit]);

  // 品名與規格是這一筆的識別,缺一不可,只打空白也算沒填。
  const canSubmit = itemName.trim() !== "" && spec.trim() !== "";

  const update = useMutation({
    mutationFn: () =>
      api.patch<CatalogHit>(`/catalog/items/${hit?.variant_id}`, {
        item_name: itemName.trim(),
        spec: spec.trim(),
        unit: unit.trim(),
        price,
      }),
    onSuccess: (saved) => {
      push(`已更新「${saved.item_name}」`, "success");
      queryClient.invalidateQueries({ queryKey: ["catalog"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      onClose();
    },
    // 失敗絕不關閉對話框:關掉等於把剛改的欄位丟掉,只能重打一次。
    // 後端的原句照抄(例如規格撞名的 409),前端不自己另編一句。
    onError: (err) => setError(err instanceof ApiError ? err.message : "無法連線到伺服器,請稍後再試。"),
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit || update.isPending) return;
    setError(null);
    update.mutate();
  };

  return (
    <Dialog
      open={hit !== null}
      onClose={onClose}
      title="編輯品項"
      maxWidth="max-w-md"
      // 改到一半誤點背景就整份消失太傷,關閉一律走 Esc 或明確的取消 / 關閉鈕。
      closeOnBackdrop={false}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <p className="m-0 text-sm text-[var(--text-2)]">
          分類:<span className="font-medium text-[var(--text)]">{hit?.category}</span>
          <span className="ml-1 text-[var(--text-3)]">(分類要換就在新分類底下另外建一筆)</span>
        </p>

        <Input
          label="品名"
          name="item_name"
          autoComplete="off"
          ref={firstFieldRef}
          required
          hint="必填。同一個品名底下的每一種規格會一起改名"
          value={itemName}
          onChange={(event) => setItemName(event.target.value)}
        />

        <Input
          label="規格"
          name="spec"
          autoComplete="off"
          required
          hint="必填。同一個品名底下不可重複"
          value={spec}
          onChange={(event) => setSpec(event.target.value)}
        />

        <div className="flex gap-3">
          <div className="w-28">
            <Input
              label="單位"
              name="unit"
              autoComplete="off"
              value={unit}
              onChange={(event) => setUnit(event.target.value)}
            />
          </div>
          <div className="min-w-0 flex-1">
            <Input
              label="單價"
              name="price"
              numeric
              hint="只收整數;改了不影響已經開出去的單"
              ref={priceField.ref}
              {...priceField.props}
            />
          </div>
        </div>

        {error ? (
          <p
            role="alert"
            className="m-0 rounded-[var(--radius)] border border-[var(--danger)] bg-[var(--surface-2)] px-2.5 py-2 text-sm text-[var(--danger)]"
          >
            {error}
          </p>
        ) : null}

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={update.isPending}>
            取消
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            loading={update.isPending}
            icon={<Save aria-hidden="true" size={16} />}
          >
            儲存修改
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
